import React, { useState } from 'react';
import { type User as UserType } from '@/types/userRole';
import ReferralCodeDisplay from '@/components/ReferralCodeDisplay';
import DashboardStatisticsComponent from './components/DashboardStatisticsComponent';
import WelcomeSection from './components/WelcomeSection';
import QuickActions from './components/QuickActions';
import EarningsSummary from './components/EarningsSummary';
import ReferralLinkCard from './components/ReferralLinkCard';
import DashboardTabs from './components/DashboardTabs';
import DashboardSidebarComponent from './components/DashboardSidebarComponent';
import { subMonths, format } from 'date-fns';

interface DashboardContentProps {
  user: UserType;
  referrals?: any[];
  points: number;
  referralCode: string;
  referralLink: string;
  reportData?: any[];
  chartData?: any[];
  lastMonthEarnings?: number;
  lifetimeEarnings?: number;
  signupDate?: string;
  currentMonth?: string;
  currentYear?: string;
  handleSendReferral: (data: any, method: string) => Promise<any>;
  exportReport?: () => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  user,
  referrals = [],
  points,
  referralCode,
  referralLink,
  reportData = [],
  chartData = [],
  lastMonthEarnings = 0,
  lifetimeEarnings = 0,
  signupDate,
  currentMonth,
  currentYear,
  handleSendReferral,
  exportReport,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const formattedDate = new Date(signupDate || Date.now()).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const last12MonthsData = Array.from({ length: 12 }).map((_, index) => {
    const date = subMonths(new Date(), index);
    const monthStr = format(date, 'MMM yyyy');
    const monthReferrals = referrals.filter(ref => 
      new Date(ref.date).getMonth() === date.getMonth() &&
      new Date(ref.date).getFullYear() === date.getFullYear()
    );

    return {
      name: monthStr,
      totalReferrals: monthReferrals.length,
      successfulReferrals: monthReferrals.filter(ref => ref.status === 'approved').length,
      earnings: monthReferrals.filter(ref => ref.status === 'approved').length * 100 // Assuming $100 per successful referral
    };
  }).reverse();

  const referralSourceData = [
    { name: 'Email', value: 40 },
    { name: 'Social Media', value: 30 },
    { name: 'Direct Link', value: 20 },
    { name: 'Other', value: 10 }
  ];
  
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  return (
    <div className="space-y-6">
      <WelcomeSection 
        user={user} 
        formattedDate={formattedDate} 
        points={points} 
      />
      
      <QuickActions activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <DashboardStatisticsComponent referrals={referrals} points={points} />
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 order-2 lg:order-1">
          <DashboardTabs 
            referrals={referrals} 
            referralLink={referralLink} 
            handleSendReferral={handleSendReferral}
            user={user}
            points={points}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            chartData={last12MonthsData}
            referralSourceData={referralSourceData}
          />
        </div>
        
        <div className="lg:col-span-1 order-1 lg:order-2">
          <DashboardSidebarComponent
            points={points}
            lastMonthEarnings={lastMonthEarnings}
            lifetimeEarnings={lifetimeEarnings}
            signupDate={formattedDate}
            referralCode={referralCode}
            referralLink={referralLink}
            referrerName={user.name}
            onSendReferral={handleSendReferral}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
